/**
 * useVisibilityPause - Automatically pauses animation when tab becomes hidden.
 * Extracted from useAnimationEngine and useAnimationInstance to eliminate duplication.
 */

import React, { useEffect } from 'react'
import type { AnimationAction, AnimationState } from '../types/Animation.types'

export type UseVisibilityPauseOptions = {
	/** Ref containing current animation state */
	stateRef: React.RefObject<AnimationState>
	/** Dispatch function for state updates */
	dispatch: React.Dispatch<AnimationAction>
}

/**
 * Hook that pauses animation when the browser tab becomes hidden.
 * This prevents animation from continuing to run in the background
 * and consuming resources.
 *
 * Uses the Page Visibility API to detect when the tab is hidden,
 * automatically pausing animation to conserve battery and CPU.
 *
 * @param stateRef - Ref containing current animation state
 * @param dispatch - Dispatch function for animation actions
 *
 * @example
 * // In useAnimationEngine
 * const stateRef = useRef(state)
 * useVisibilityPause({ stateRef, dispatch })
 *
 * @example
 * // In useAnimationInstance
 * const stateRef = useRef(state)
 * useEffect(() => { stateRef.current = state }, [state])
 * useVisibilityPause({ stateRef, dispatch })
 */
export function useVisibilityPause({
									   stateRef,
									   dispatch,
								   }: UseVisibilityPauseOptions): void {
	useEffect(() => {
		function handleVisibilityChange() {
			if (document.hidden && stateRef.current?.isPlaying) {
				dispatch({ type: 'PAUSE' })
			}
		}

		document.addEventListener('visibilitychange', handleVisibilityChange)
		return () => {
			document.removeEventListener('visibilitychange', handleVisibilityChange)
		}
	}, [stateRef, dispatch])
}
